import { positionChecker } from 'src/logic'
import { ysrDirectLogo1LabelObj } from 'src/features/yellStory/constants/colors'
import { I18NText, Position, State, StateObj } from 'src/types'
import { getBaseCell, getColorCell } from 'src/util/simulationsSpecification'

const genCellContent = (head: string, content: string, alignment?: string, color?: string) => [
  { text: `${head}： `, fontSize: 8 },
  {
    text: content,
    alignment: alignment || 'right',
    fontSize: 10,
    color: color || '#323232'
  }
]

export const genBaseCells = (state: State, position: Position, t: I18NText) => {
  const genGloveBaseCell = (head: string, stateObj: StateObj) => ({ head, label: stateObj?.label, value: stateObj?.value })
  const { isGlove } = positionChecker(position)
  const productNumber = state.baseModel.productNumber
  const baseModelStateObj = {
    label: productNumber,
    value: productNumber
  }
  return [
    genGloveBaseCell('基本モデル', baseModelStateObj),
    genGloveBaseCell('シリーズ', state.series),
    genGloveBaseCell('利き腕', state.dominantArm),
    genGloveBaseCell('指の長さ', state.size),
    genGloveBaseCell('指あて', state.fingerGuardType),
    genGloveBaseCell('革一体形状(人差指)', state.leatherIntegratedIndex),
    genGloveBaseCell('革一体形状(中指)', state.leatherIntegratedMiddle),
    genGloveBaseCell('革一体形状(薬指)', state.leatherIntegratedRing),
    genGloveBaseCell('芯トジスタイル', state.materialPack),
    isGlove && genGloveBaseCell('ウェブパーツ', state.webParts)
  ].filter(Boolean)
}

export const getYellStoryOptionalEmbroideryCells = (state: State) => {
  const genCell = (head: string, stateObj: StateObj) => ({ head, label: stateObj?.label, value: stateObj?.value })
  return [
    genCell('', state.ysrOriginalEmbroidery),
    genCell('', state.ysrDirectLogo1Position),
    state.ysrDirectLogo1Position.value !== 'none' && genCell('', state.ysrDirectLogo1MainColor),
    state.ysrDirectLogo1Position.value !== 'none' && genCell('', state.ysrDirectLogo1EdgeColor),
    genCell('', state.ysrDirectLogo2Position),
    state.ysrDirectLogo2Position.value !== 'none' && genCell('', state.ysrDirectLogo2Color)
  ].filter(Boolean)
}

// atoms以外
export const checkYellStoryWeb2 = (webPartsValue: string): boolean =>
  ['Yw1', 'Yw2', 'Yw3', 'Yw4', 'Yw5', 'Yw6', 'Yw7', 'Yw8', 'Yw9', 'Yw10', 'Yw11', 'Yw12', 'Yw13', 'Yw14', 'Yw16', 'Yw17'].includes(webPartsValue)

export const getYellStoryColorCells = (state: State, t: I18NText) => {
  const needFingerCoverOrPad = state.fingerGuardType?.value !== 'none'
  const needWeb2 = checkYellStoryWeb2(state?.webParts?.value)
  const isIntegratedIndex = state?.leatherIntegratedIndex?.value === 'atIndexFinger' || state?.hamidashiCut?.value === 'hamidashiCut'
  const isIntegratedMiddle = state?.leatherIntegratedMiddle?.value === 'atMiddleFinger'
  const isIntegratedRing = state?.leatherIntegratedRing?.value === 'atRingFinger'
  const isLoopOfRingFinger = state?.loopOfRingFinger?.value === 'loopOfRingFinger'
  const isBelt = state?.beltType?.value !== 'thumbIntegrated'

  return [
    getColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    getColorCell(t.LACE, state.lace, 'lace'),
    getColorCell(t.WEB, state.web, 'web'),
    needWeb2 && getColorCell(t.WEB2, state.web2, 'web2'),
    getColorCell(t.EDGE, state.edge, 'edge'),
    getColorCell(t.STITCH, state.stitch, 'stitch'),
    getColorCell(t.HAMIDASHI, state.hamidashi, 'hamidashi'),
    getColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    getColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && getColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    getColorCell(t.LININGS, state.linings, 'linings'),
    getColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
    getColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
    getColorCell(`${t.INDEX}${isIntegratedIndex ? '3-1' : '3'}`, state.indexWeb, 'indexWeb'),
    !isIntegratedIndex && getColorCell(t.INDEX4, state.indexMiddle, 'indexMiddle'), // state.leatherIntegratedIndexで消える
    getColorCell(`${t.MIDDLE}${isIntegratedMiddle ? '4-1' : '5'}`, state.middleIndex, 'middleIndex'),
    !isIntegratedMiddle && getColorCell(t.MIDDLE6, state.middleRing, 'middleRing'), // state.leatherIntegratedMiddleで消える
    getColorCell(`${t.RING}${isIntegratedRing ? '5-1' : '7'}`, state.ringMiddle, 'ringMiddle'),
    !isIntegratedRing && getColorCell(t.RING8, state.ringLittle, 'ringLittle'), // state.leatherIntegratedRingで消える
    getColorCell(t.LITTLE9, state.littleRing, 'littleRing'),
    getColorCell(t.LITTLE10, state.littleOut, 'littleOut'),
    isBelt && getColorCell(t.BAND, state.listBelt, 'listBelt'),
    isLoopOfRingFinger && getColorCell(t.LOOP_OF_RING, state.loopOfRingFingerColor, 'loopOfRingFingerColor'),

    state.ysrDirectLogo1Position.value !== 'band'
      ? getColorCell('Yell storyラベル', state.ysrLabel, 'ysrLabel')
      : getColorCell('Yell storyラベル', ysrDirectLogo1LabelObj, 'ysrLabel')
  ].filter(Boolean)
}

export const getYellStoryBaseCells = (state: State) => {
  const baseModelStateObj = {
    label: state.baseModel.productNumber,
    value: state.baseModel.productNumber
  }
  return [
    getBaseCell('基本モデル', baseModelStateObj),
    getBaseCell('シリーズ', state.series),
    getBaseCell('利き腕', state.dominantArm),
    getBaseCell('指の長さ', state.size),
    getBaseCell('指あて', state.fingerGuardType),
    getBaseCell('革一体形状(人差指)', state.leatherIntegratedIndex),
    getBaseCell('革一体形状(中指)', state.leatherIntegratedMiddle),
    getBaseCell('革一体形状(薬指)', state.leatherIntegratedRing),
    getBaseCell('芯トジスタイル', state.materialPack),
    getBaseCell('ウェブパーツ', state.webParts)
  ].filter(Boolean)
}

const item = (title: string) => [
  {
    text: title,
    style: { fontSize: 11 },
    margin: [0, 8, 0, 4]
  }
]
export const yellStoryOriginalEmbroiderySettings = (state: State): any => {
  const existDirectLogo1 = state.ysrDirectLogo1Position.value !== 'none'
  const directLogo1 = existDirectLogo1
    ? [
        {
          text: `ダイレクトロゴ1`,
          style: { fontSize: 11 },
          margin: [0, 8, 0, 4]
        },
        {
          table: {
            widths: '33%',
            body: [
              [
                genCellContent(`ダイレクトロゴ1`, state.ysrDirectLogo1Position.label),
                genCellContent(`ダイレクトロゴ1メインカラー`, state.ysrDirectLogo1MainColor.label),
                genCellContent(`ダイレクトロゴ1フチカラー`, state.ysrDirectLogo1EdgeColor.label)
              ]
            ]
          }
        }
      ]
    : item('ダイレクトロゴ1 / なし')
  const existDirectLogo2 = state.ysrDirectLogo2Position.value !== 'none'
  const directLogo2 = existDirectLogo2
    ? [
        {
          text: `ダイレクトロゴ2`,
          style: { fontSize: 11 },
          margin: [0, 8, 0, 4]
        },
        {
          table: {
            widths: '50%',
            body: [
              [
                genCellContent(`ダイレクトロゴ2`, state.ysrDirectLogo2Position.label),
                genCellContent(`ダイレクトロゴ2カラー`, state.ysrDirectLogo2Color.label)
              ]
            ]
          }
        }
      ]
    : item('ダイレクトロゴ2 / なし')
  const existOriginalEmbroidery = state.ysrOriginalEmbroidery.value !== 'none'
  const title = existOriginalEmbroidery ? 'オリジナル刺繍 / あり' : 'オリジナル刺繍 / なし'
  const originalEmbroidery = item(title)

  return [...directLogo1, ...directLogo2, ...originalEmbroidery]
}
